import { useEffect, useState } from "react";
import { api, type LinjaMateriaalit as LM } from "./api";
import Liitteet from "./Liitteet";
import IsometriMerkinnat from "./IsometriMerkinnat";
import { t } from "./kieli";

/* Linjan isometri + materiaalit hitsauslokin istunnossa. Hitsaaja näkee
   isometri-PDF:n (liite) ja mitä materiaaleja linjalle kuuluu (isometri-BOM)
   sekä mitä on jo hitsattu (as-built osat). */

export default function LinjanMateriaalit({
  linjaId, tunnus, saaLisata, saaPoistaa,
}: { linjaId: number; tunnus: string; saaLisata: boolean; saaPoistaa: boolean }) {
  const [auki, setAuki] = useState(false);
  const [isoAuki, setIsoAuki] = useState(false);
  const [d, setD] = useState<LM | null>(null);

  useEffect(() => {
    if (auki && !d) api.linjaMateriaalit(linjaId).then(setD).catch(() => setD(null));
  }, [auki, linjaId, d]);

  return (
    <section className="panel" style={{ marginTop: 16 }}>
      <div className="panel__head">
        <h2>{t("muu.lmat.otsikko")}</h2>
        <button className="nappi nappi--kevyt" onClick={() => setAuki(!auki)}>{auki ? t("hl.sulje") : t("qr.avaa")}</button>
      </div>

      {auki && (
        <div style={{ padding: "2px 2px 4px" }}>
          <button className="nappi nappi--kevyt" onClick={() => setIsoAuki(!isoAuki)}>
            📐 {isoAuki ? t("muu.lmat.piilota_iso") : t("muu.lmat.iso_n", { s: tunnus })}
          </button>
          {isoAuki && (
            <div style={{ marginTop: 8 }} data-hitsausloki-isometri={linjaId}>
              {/* 7.10.2026 (mitattu koodista, käyttöohjeen tarkistus): isometrituonnilla tuodut kuvat (Sweco, Vertex-kirja)
                  tallentuvat isometriin eivätkä linjan liitteeksi, joten tämä paneeli näytti hitsaajalle «Ei liitteitä» vaikka
                  isometri oli Selailussa. Sama näkymä kuin Selailussa, vain luku (merkinnät tehdään Selailussa). */}
              <IsometriMerkinnat linjaId={linjaId} saaKirjata={false} />
              <Liitteet kohdeTaulu="linja" kohdeId={linjaId}
                        saaMuokata={saaLisata} saaPoistaa={saaPoistaa} otsikko={t("nippu.isometri")} />
            </div>
          )}

          {!d && <p className="dim" style={{ marginTop: 10 }}>{t("yleinen.ladataan")}</p>}
          {d && (
            <div style={{ marginTop: 12 }}>
              {d.osaluettelo && d.osaluettelo.length > 0 && (
                <>
                  <h3>{t("muu.lmat.osaluettelo", { n: d.osaluettelo.length })}</h3>
                  <div style={{ overflowX: "auto" }}>
                    <table className="tbl" style={{ fontSize: 12.5, minWidth: 460 }}>
                      <thead><tr>
                        <th>#</th><th>{t("hl.nimike")}</th><th>DN</th><th>{t("muu.lmat.mitta")}</th>
                        <th>{t("hl.materiaali")}</th><th style={{ textAlign: "right" }}>{t("muu.lmat.lkm")}</th><th style={{ textAlign: "right" }}>{t("muu.lmat.pituus")}</th>
                      </tr></thead>
                      <tbody>
                        {d.osaluettelo.map((o, i) => (
                          <tr key={i}>
                            <td className="mono dim">{o.osa_nro ?? ""}</td>
                            <td>{o.nimike}</td>
                            <td className="mono">{o.dn ?? ""}</td>
                            <td className="mono dim">{o.mitta ?? ""}</td>
                            <td>{o.materiaali ?? ""}</td>
                            <td className="mono" style={{ textAlign: "right" }}>{o.lkm ?? ""}</td>
                            <td className="mono" style={{ textAlign: "right" }}>{o.pituus_m ? `${o.pituus_m} m` : ""}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {d.tarve.length > 0 && (
                <>
                  <h3>{t("muu.lmat.tarve_otsikko")}</h3>
                  <div style={{ overflowX: "auto" }}>
                    <table className="tbl" style={{ fontSize: 12.5, minWidth: 360 }}>
                      <thead><tr><th>{t("hl.nimike")}</th><th style={{ textAlign: "right" }}>{t("muu.lmat.tarve")}</th></tr></thead>
                      <tbody>
                        {d.tarve.map((tr) => (
                          <tr key={tr.nimike_tunnus}>
                            <td>{tr.nimike_tunnus}{tr.nimi ? ` · ${tr.nimi}` : ""}</td>
                            <td className="mono" style={{ textAlign: "right" }}>{tr.tarve} {tr.yksikko}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {d.osat.length > 0 && (
                <>
                  <h3 style={{ marginTop: 14 }}>{t("muu.lmat.hitsatut")}</h3>
                  <div style={{ overflowX: "auto" }}>
                    <table className="tbl" style={{ fontSize: 12.5, minWidth: 360 }}>
                      <thead><tr><th>{t("hl.nimike")}</th><th>{t("hl.materiaali")}</th><th style={{ textAlign: "right" }}>{t("muu.lmat.kpl")}</th><th>{t("hl.sulatukset")}</th></tr></thead>
                      <tbody>
                        {d.osat.map((o, i) => (
                          <tr key={i}>
                            <td>{o.nimike}</td>
                            <td>{o.materiaali ?? "—"}</td>
                            <td className="mono" style={{ textAlign: "right" }}>{o.kpl}</td>
                            <td className="mono dim" style={{ fontSize: 11, overflowWrap: "anywhere" }}>{o.sulatukset ?? "—"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}

              {d.tarve.length === 0 && d.osat.length === 0 && (!d.osaluettelo || d.osaluettelo.length === 0) && (
                <p className="dim">{t("muu.lmat.ei_tietoja")}</p>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
}
